import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Input,
    Skeleton,
    Typography,
    message,
    Space,
    Tag
} from 'antd';
import type {
    ColumnsType,
    TablePaginationConfig,
    SorterResult,
    SortOrder
} from 'antd/es/table/interface';
import { CheckCircleOutlined, ClockCircleOutlined, FileOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import FormButton from '../components/common/formbutton';
import SummaryCard from '../components/common/summarycard';
import PageVersionApprovalDetailsDrawer from '../components/approvals/pageversionapprovaldetailsdrawer';
import { getPagedPageVersionApprovals } from '../services/pageversionservice';

const { Search } = Input;
const { Text } = Typography;

interface PageVersionApproval {
    guid: string;
    pageVersionName: string;
    requestedBy: string;
    status: string;
    createdOn: string;
    reviewedBy?: string | null;
    reviewedOn?: string | null;
}

interface PagedResult<T> {
    data: T[];
    totalCount: number;
    approvedCount: number;
    pendingCount: number;
    rejectedCount: number;
}

const PageVersionApprovalRequests: React.FC = () => {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [approvals, setApprovals] = useState<PageVersionApproval[]>([]);
    const [totalApprovals, setTotalApprovals] = useState<number>(0);
    const [approvedCount, setApprovedCount] = useState<number>(0);
    const [pendingCount, setPendingCount] = useState<number>(0);
    const [rejectedCount, setRejectedCount] = useState<number>(0);

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortedInfo, setSortedInfo] = useState<SorterResult<PageVersionApproval>>({});

    const [messageApi, contextHolder] = message.useMessage();

    const commonSortDirections: SortOrder[] = ['ascend', 'descend', 'ascend'];
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);

    const handleView = (id: string) => {
        setSelectedApprovalId(id);
        setDrawerOpen(true);
    };

    const fetchApprovals = useCallback(async () => {
        setLoading(true);
        try {
            const sortOrderParam =
                sortedInfo.order === 'ascend' ? 'ascend' :
                    sortedInfo.order === 'descend' ? 'descend' : undefined;

                    const data = await getPagedPageVersionApprovals({
                        PageNumber: currentPage,
                        PageSize: pageSize,
                        SearchText: searchText,
                        SortBy: sortedInfo.columnKey,
                        SortOrder: sortOrderParam,
                      });
                      
                      setApprovals(data.data);
                      setTotalApprovals(data.totalCount);
                      setApprovedCount(data.approvedCount);
                      setPendingCount(data.pendingCount);
                      setRejectedCount(data.rejectedCount);
        } catch (error) {
            console.error('Error fetching approvals:', error);
            messageApi.error('Failed to load approval requests.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchText, sortedInfo, messageApi]);

    useEffect(() => {
        fetchApprovals();
    }, [fetchApprovals]);

    const handleTableChange = (
        pagination: TablePaginationConfig,
        _: Record<string, any>,
        sorter: SorterResult<PageVersionApproval> | SorterResult<PageVersionApproval>[]
    ) => {
        if (pagination.current !== currentPage) setCurrentPage(pagination.current || 1);
        if (pagination.pageSize !== pageSize) {
            setPageSize(pagination.pageSize || 10);
            setCurrentPage(1);
        }

        const sort = Array.isArray(sorter) ? sorter[0] : sorter;
        if (sort.columnKey && sort.order) {
            setSortedInfo({ columnKey: sort.columnKey as string, order: sort.order });
        } else {
            setSortedInfo({});
        }
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        setCurrentPage(1);
        setSortedInfo({});
    };

    const columns: ColumnsType<PageVersionApproval> = [
        {
            title: 'PageVersion Name',
            dataIndex: 'pageVersionName',
            key: 'pageVersionName',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'pageVersionName' ? sortedInfo.order : undefined,
            render: (text: string) => <Text>{text}</Text>,
        },
        {
            title: 'Requested By',
            dataIndex: 'requestedBy',
            key: 'requestedBy',
        },
        {
            title: 'Requested On',
            dataIndex: 'createdOn',
            key: 'createdOn',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'createdOn' ? sortedInfo.order : undefined,
            render: (date: string) => new Date(date).toLocaleDateString(),
        },
        {
            title: 'Reviewed By',
            dataIndex: 'reviewedBy',
            key: 'reviewedBy',
            render: (reviewedBy: string | null | undefined) => {
                if (!reviewedBy) {
                    return <Tag color="orange">Pending</Tag>;
                }
                return <Text>{reviewedBy}</Text>;
            }
        },

        {
            title: 'Reviewed On',
            dataIndex: 'reviewedOn',
            key: 'reviewedOn',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'reviewedOn' ? sortedInfo.order : undefined,
            render: (date: string | null | undefined) =>
                date ? new Date(date).toLocaleDateString() : <Tag color="orange">Pending</Tag>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: true,
            sortDirections: commonSortDirections,
            sortOrder: sortedInfo.columnKey === 'status' ? sortedInfo.order : undefined,
            render: (status: string) => {
                const color =
                    status === 'Approved' ? 'green' :
                        status === 'Pending' ? 'orange' :
                            status === 'Rejected' ? 'red' :
                                status === 'Failed' ? 'red' : 'default';
                return <Tag color={color}>{status}</Tag>;
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {record.status === 'Pending' && (
                        <FormButton
                            title="Review"
                            onClick={() => navigate(`/approvals/review/${record.guid}`)}
                        />
                    )}

                    {record.status !== 'Pending' && (
                        <FormButton
                            title="View Request"
                            onClick={() => handleView(record.guid)}
                        />
                    )}
                </Space>
            )
        }
    ];

    const cardData = [
        {
            title: 'Total Requests',
            value: totalApprovals,
            icon: <FileOutlined style={{ color: 'var(--color-primary)' }} />,
            cardKey: 'total',
        },
        {
            title: 'Approved',
            value: approvedCount,
            icon: <CheckCircleOutlined style={{ color: 'var(--color-primary)' }} />,
            cardKey: 'approved',
        },
        {
            title: 'Pending',
            value: pendingCount,
            icon: <ClockCircleOutlined style={{ color: 'var(--color-primary)' }} />,
            cardKey: 'pending',
        },
        {
            title: 'Rejected',
            value: rejectedCount,
            icon: <CloseCircleOutlined style={{ color: 'var(--color-error)' }} />,
            cardKey: 'rejected',
        },
    ];

    return (
        <div>
            {contextHolder}

            <Typography.Title level={2}>PageVersion Approval Requests</Typography.Title>
            <SummaryCard cards={cardData} loading={loading} />
            <Space direction="vertical" style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Search by page name or requester"
                    onSearch={handleSearch}
                    onChange={(e) => setSearchText(e.target.value)}
                    value={searchText}
                    style={{ width: 400, marginBottom: 10 }}
                    allowClear
                />
            </Space>
            {loading ? (
                <Skeleton active />
            ) : (
                <Table
                    dataSource={approvals}
                    columns={columns}
                    rowKey="guid"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalApprovals,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={handleTableChange}
                />
            )}
            <PageVersionApprovalDetailsDrawer
                approvalId={selectedApprovalId}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            />
        </div>
    );
};

export default PageVersionApprovalRequests;